import React, { FC, useEffect, useMemo, useState } from "react";
import { DashedButton, Notification, Heading, Text, toast } from "@telia/teddy";
import cn from "classnames";
import { connect, useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ExistingMemberCard from "./ExistingMemberCard";
import ExtraSubscriptionModal from "./ExtraSubscriptionModal";
import TeliaTryggModal from "./TeliaTryggModal";
import Member from "./Member";
import { getMainSubscription, isCartSubscription, isExistingSubscription } from "./utils";
import { CUSTOMER_TYPE, CustomerType } from "../../../../../constants";
import { RootState, EsimType, StepIndicatorType } from "../../../../../models";
import { AccountSubscription, SubscriptionsState } from "../../../../../models/state";
import * as properNameCase from "../../../../../shared/properNameCase";
import { trackWebshopClickEvent } from "../../../google-analytics/googleAnalyticsEventHelper";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import CheckoutPage from "../../components/organisms/CheckoutPage/CheckoutPage";
import {
    chooseMemberPhoneNumber,
    chooseMemberPhoneNumberSwitchPanel,
    fetchAvailableNumbers,
    prefillMemberform,
    setSelectedPhoneNumber,
    setSelectedStep,
} from "../../state/actions/addMemberActions";
import { addSubscriptionToCart, setActiveSubscription } from "../../state/actions/cartActions";
import { setValue as setPersonalDetailsValue } from "../../state/actions/personalDetailsActions";
import {
    CHECKOUT_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
    CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS,
} from "../../state/actionTypes";
import {
    hasExternalLegalOwner as gotExternalLegalOwner,
    getAccountType,
    getExistingSubscriptionsInGroup,
} from "../../state/selectors/accountSelectors";
import {
    getActiveSubscription,
    getHardwareEsimTypeForCustomerType,
    isLegalSubscriptionDraft as isLegalSubscriptionDraftSelector,
    hasOnlyServiceSubscriptions as hasOnlyServiceSubscriptionsSelector,
} from "../../state/selectors/cartSelectors";
import { getPhoneNumbersInCart } from "../../state/selectors/combinedSelectors";
import { getLegalOwnerFullName } from "../../state/selectors/customerInfoSelectors";
import getStepIndicatorData from "../../state/selectors/getStepIndicatorData";
import { getSelectedStep, isMandatoryMemberFieldsCompleted } from "../../state/selectors/memberSelector";
import { getPhoneNumberCheckType } from "../../state/selectors/phoneNumberSelectors";
import {
    getMobileSubscriptions,
    isEsimSelected,
    isProductUserMandatory,
    isServiceSubscription,
    isTeliaTryggSubscription,
} from "../../state/selectors/subscriptionsSelectors";
import "./AddMember.less";
import { fulfilled } from "../../utils";

interface StateProps {
    accountType?: CustomerType;
    activeSubscription?: string;
    activeSubscriptionHasEsim: boolean;
    activeSubscriptionIsService: boolean;
    customerType?: CustomerType;
    esimType?: EsimType;
    existingSubscriptions?: AccountSubscription[];
    hasExternalLegalOwner: boolean;
    hasOnlyServiceSubscriptions: boolean;
    isFetchingData: boolean;
    isLegalSubscriptionDraft: boolean;
    isProductUserMandatoryForActiveSubscription: boolean;
    isReadyToContinue: boolean;
    isTeliaTrygg: boolean;
    legalOwnerName: string;
    stepIndicator: StepIndicatorType;
    subscriptions: SubscriptionsState;
}

type Props = StateProps;

const AddMember: FC<Props> = ({
    // Redux
    isFetchingData,
    hasExternalLegalOwner,
    activeSubscription,
    subscriptions,
    isLegalSubscriptionDraft,
    isReadyToContinue,
    accountType,
    isTeliaTrygg,
    activeSubscriptionHasEsim,
    activeSubscriptionIsService,
    customerType,
    esimType,
    stepIndicator,
    legalOwnerName,
    hasOnlyServiceSubscriptions,
    existingSubscriptions,
}) => {
    const dispatch = useDispatch();
    const location = useLocation();

    const phoneNumbersInCart = useSelector((state: RootState) => getPhoneNumbersInCart(state));
    const [showExtraSubscriptionModal, setShowExtraSubscriptionModal] = useState(false);
    const [showTeliaTryggModal, setShowTeliaTryggModal] = useState(false);
    const filteredExistingSubscriptions = useMemo(() => {
        if (!existingSubscriptions) {
            return [];
        }
        return existingSubscriptions.filter((sub) => !phoneNumbersInCart.includes(sub.phoneNumber));
    }, [existingSubscriptions, phoneNumbersInCart]);

    useEffect(() => {
        dispatch(setPersonalDetailsValue("esimType", esimType));
        dispatch(setPersonalDetailsValue("isEsimSelected", activeSubscriptionHasEsim));
    }, [activeSubscription, customerType]);

    useEffect(() => {
        if (isLegalSubscriptionDraft) {
            if (accountType === CUSTOMER_TYPE.NEW) {
                dispatch(chooseMemberPhoneNumberSwitchPanel(CUSTOMER_TYPE.NEW, activeSubscription));
            } else if (!activeSubscriptionIsService) {
                dispatch(chooseMemberPhoneNumber("", activeSubscription));
            }
        }
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, []);

    const handleSetShowExtraSubscriptionModal = (show: boolean, addedSubscriptionId?: string) => {
        if (!show) {
            if (addedSubscriptionId) {
                dispatch(setPersonalDetailsValue("isProductUserDifferentThanLegalOwner", true));
                dispatch(setPersonalDetailsValue("isCatalogListingOk", false));
                dispatch(setPersonalDetailsValue("isSecretNumber", false));
                dispatch(setPersonalDetailsValue("isCampaignToggled", false));
                dispatch(setPersonalDetailsValue("campaignIdsNotAllowed", []));
                dispatch(setPersonalDetailsValue("isOptingOutForTeliaXServices", false));
                dispatch(setPersonalDetailsValue("isEmailTakenForTeliaTrygg", false));
                dispatch(setPersonalDetailsValue("isEmailTakenForTeliaSky", false));
                dispatch(setSelectedPhoneNumber("", CUSTOMER_TYPE.NEW, addedSubscriptionId));
                dispatch({
                    type: fulfilled(CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS),
                    payload: [],
                });
                dispatch({
                    type: CHECKOUT_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
                    activePanel: CUSTOMER_TYPE.EXISTING,
                });
                dispatch(setActiveSubscription(addedSubscriptionId));
                dispatch(fetchAvailableNumbers(true, addedSubscriptionId));
                dispatch(prefillMemberform(addedSubscriptionId, true));
                dispatch(setSelectedStep("phoneNumber"));
            }
        }

        if (show) {
            trackWebshopClickEvent({
                action: "click",
                text: "Legg til ekstra abonnement",
                type: "button",
                url: "/kasse/medlem",
                context: "open_extra_modal",
            });
        }

        setShowExtraSubscriptionModal(show);
    };

    const handleSetShowTeliaTryggModal = (display: boolean, addedSubscriptionId?: string) => {
        if (!display) {
            if (addedSubscriptionId) {
                dispatch(setPersonalDetailsValue("isCatalogListingOk", false));
                dispatch(setPersonalDetailsValue("isSecretNumber", false));
                dispatch(setPersonalDetailsValue("isCampaignToggled", false));
                dispatch(setPersonalDetailsValue("campaignIdsNotAllowed", []));
                dispatch(setActiveSubscription(addedSubscriptionId));
                dispatch(setSelectedStep("info"));
            }
        }
        if (display) {
            trackWebshopClickEvent({
                action: "click",
                text: "Legg til telia trygg",
                type: "button",
                url: "/kasse/medlem",
                context: "open_telia_trygg_modal",
            });
        }

        setShowTeliaTryggModal(display);
    };

    let pageIndex = 2;
    if (hasExternalLegalOwner && !hasOnlyServiceSubscriptions) {
        pageIndex = 3;
    }

    const showFamilyHierarchy = isReadyToContinue && !activeSubscription;
    const allSubscriptions = useMemo(() => {
        const allSubs = [...Object.values(subscriptions), ...(filteredExistingSubscriptions || [])];
        const mainSub = getMainSubscription(allSubs);
        return mainSub ? [mainSub, ...allSubs.filter((sub) => sub !== mainSub)] : allSubs;
    }, [subscriptions, filteredExistingSubscriptions]);

    return (
        <CheckoutPage
            isFetchingData={isFetchingData}
            isReadyToContinue={isReadyToContinue}
            pageIndex={pageIndex}
            isTeliaTrygg={isTeliaTrygg}
            stepIndicator={stepIndicator}
            path={location.pathname}
        >
            <div className="add-member__heading">
                <Text as="p">Eier og betaler</Text>
                <Heading mb="100" as="h1" variant="title-100" data-di-mask>
                    {properNameCase.convert(legalOwnerName)}
                </Heading>
            </div>
            <div className={cn({ "add-member__indented-members": showFamilyHierarchy })}>
                {allSubscriptions.map((sub, index) => {
                    if (isCartSubscription(sub)) {
                        return (
                            <div
                                className={cn({
                                    "add-member__indented-members__item": showFamilyHierarchy && index > 0,
                                    "add-member__indented-members__item-main": showFamilyHierarchy && index === 0,
                                })}
                                key={`registered-member-${sub.id}`}
                            >
                                <Member index={index + 1} subscriptionId={sub.id} />
                            </div>
                        );
                    } else if (isReadyToContinue && isExistingSubscription(sub)) {
                        return (
                            <div
                                className={cn({
                                    "add-member__indented-members__item": showFamilyHierarchy && index > 0,
                                    "add-member__indented-members__item-main": showFamilyHierarchy && index === 0,
                                })}
                                key={`registered-member-${sub.phoneNumber}`}
                            >
                                <ExistingMemberCard existingSubscription={sub} />
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
            {isReadyToContinue && (
                <>
                    {allSubscriptions.length >= 8 && (
                        <Notification type="tile" variant="information" ml="200" mr="200" mt="300">
                            <Notification.Icon name="error-filled" />
                            <Notification.Text>Maks antall abonnement per faktura er 8 stk.</Notification.Text>
                        </Notification>
                    )}
                    <DashedButton
                        onClick={() => setShowExtraSubscriptionModal(true)}
                        className="add-member__extra-button"
                        disabled={allSubscriptions.length >= 8}
                    >
                        <>
                            <div>
                                <Heading className="add-member__extra-button__heading" asChild variant="title-100">
                                    <div>Legg til flere abonnement</div>
                                </Heading>
                                <Text className="add-member__extra-button__subtitle">Få rabatt på hvert ekstra!</Text>
                            </div>
                        </>
                    </DashedButton>
                    <DashedButton
                        onClick={() => setShowTeliaTryggModal(true)}
                        className="add-member__extra-button"
                        disabled={allSubscriptions.length >= 8}
                        icon="secured-1"
                    >
                        <>
                            <div className="add-member__extra-button__wrapper">
                                <Heading className="add-member__extra-button__heading" asChild variant="title-100">
                                    <div>Prøv Telia Trygg gratis i 3 md.</div>
                                </Heading>
                                <Text className="add-member__extra-button__subtitle">
                                    Trygghetstjeneste mot svindel og ID-tyveri
                                </Text>
                            </div>
                        </>
                    </DashedButton>
                </>
            )}
            {showExtraSubscriptionModal && (
                <ExtraSubscriptionModal
                    setIsOpen={(open) => handleSetShowExtraSubscriptionModal(open)}
                    isOpen={showExtraSubscriptionModal}
                    onClose={() => handleSetShowExtraSubscriptionModal(false)}
                    onSubmit={async (offeringCode, quantity) => {
                        const response = await dispatch(addSubscriptionToCart(offeringCode, quantity));
                        const newSubscriptionId = response.cartItemId;
                        if (newSubscriptionId) {
                            toast.success(`Abonnementet er lagt til`, {
                                position: "top-center",
                            });
                            trackWebshopClickEvent({
                                action: "click",
                                text: "Velg abonnement",
                                type: "button",
                                url: "/kasse/medlem",
                                context: "add_extra_subscription_from_modal",
                            });

                            handleSetShowExtraSubscriptionModal(false, newSubscriptionId);
                        }
                    }}
                />
            )}
            {showTeliaTryggModal && (
                <TeliaTryggModal
                    setIsOpen={(open) => handleSetShowTeliaTryggModal(open)}
                    isOpen={showTeliaTryggModal}
                    onClose={() => handleSetShowTeliaTryggModal(false)}
                    onSubmit={async (offeringCode, quantity) => {
                        const response = await dispatch(addSubscriptionToCart(offeringCode, quantity));
                        const newSubscriptionId = response.cartItemId;
                        if (newSubscriptionId) {
                            toast.success(`Telia Trygg er lagt til`, {
                                position: "top-center",
                            });
                            trackWebshopClickEvent({
                                action: "click",
                                text: "Velg telia trygg",
                                type: "button",
                                url: "/kasse/medlem",
                                context: "add_telia_trygg_from_modal",
                            });

                            handleSetShowTeliaTryggModal(false, newSubscriptionId);
                        }
                    }}
                />
            )}
        </CheckoutPage>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => {
    const activeSubscription = getActiveSubscription(state);
    const customerType = getPhoneNumberCheckType(state, activeSubscription);
    const props: StateProps = {
        isFetchingData: LOADING_SELECTORS.isLoading(state),
        hasExternalLegalOwner: gotExternalLegalOwner(state),
        accountType: getAccountType(state),
        existingSubscriptions: getExistingSubscriptionsInGroup(state),
        activeSubscription,
        isLegalSubscriptionDraft: isLegalSubscriptionDraftSelector(state),
        subscriptions: getMobileSubscriptions(state),
        isTeliaTrygg: isTeliaTryggSubscription(state, activeSubscription),
        activeSubscriptionHasEsim: isEsimSelected(state, activeSubscription),
        activeSubscriptionIsService: isServiceSubscription(state, activeSubscription),
        esimType: getHardwareEsimTypeForCustomerType(state, activeSubscription, customerType),
        stepIndicator: getStepIndicatorData(state),
        isProductUserMandatoryForActiveSubscription: isProductUserMandatory(state, activeSubscription),
        customerType,
        legalOwnerName: getLegalOwnerFullName(state),
        isReadyToContinue: isMandatoryMemberFieldsCompleted(state) && getSelectedStep(state) === undefined,
        hasOnlyServiceSubscriptions: hasOnlyServiceSubscriptionsSelector(state),
    };
    return props;
};

export default connect(mapStateToProps)(AddMember);
